import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Input, Label, Form, Row, Col, Card, CardBody, InputGroup, InputGroupText, FormGroup } from "reactstrap";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { FiDollarSign, FiEdit, FiFileText } from "react-icons/fi";

const EditPayment = () => {
    const { id } = useParams(); // payment_id
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [paymentData, setPaymentData] = useState({
        amount: "",
        payment_method: "cash",
        note: ""
    });

    const [meta, setMeta] = useState({
        total_with_gst: 0,
        paid_without_this: 0,
        allowed_max: 0
    });

    useEffect(() => {
        const fetchPayment = async () => {
            dispatch(loadingTrue());
            try {
                const res = await Handler({ method: "GET", url: `/purchase_payments/view.php?id=${id}` });
                if (res?.success) {
                    const p = res.data.payment;
                    const summary = res.data.payment_summary;
                    
                    setPaymentData({
                        amount: p.amount,
                        payment_method: p.payment_method,
                        note: p.note || ""
                    });

                    // Logic: Remaining allowance = (Total - (Paid - Current))
                    const paidWithoutThis = summary.total_paid - p.amount;
                    setMeta({
                        total_with_gst: summary.total_amount,
                        paid_without_this: paidWithoutThis,
                        allowed_max: summary.total_amount - paidWithoutThis
                    });
                }
            } catch (err) {
                toast.error("Failed to load payment");
            } finally {
                dispatch(loadingFalse());
            }
        };
        fetchPayment();
    }, [id, dispatch]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (parseFloat(paymentData.amount) > meta.allowed_max) {
            return Swal.fire("Limit Exceeded", `Max allowed amount is ₹${meta.allowed_max}`, "error");
        }

        dispatch(loadingTrue());
        try {
            const res = await Handler({
                method: "POST",
                url: "/payment/update.php", // Make sure this matches your PHP file
                data: {
                    payment_id: parseInt(id),
                    ...paymentData
                }
            });

            if (res?.success) {
                Swal.fire("Success", "Payment updated successfully", "success");
                navigate(-1);
            } else {
                toast.error(res?.message || "Update failed");
            }
        } catch (err) {
            toast.error("Server error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    return (
        <div className="page-container p-3 p-md-4 bg-light min-vh-100">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="fw-bold text-theme">Edit Payment Record</h3>
                <Button color="white" className="shadow-sm border-0 rounded-pill" onClick={() => navigate(-1)}>Back</Button>
            </div>

            <Row className="justify-content-center">
                <Col lg={6}>
                    <Card className="border-0 shadow-sm rounded-4">
                        <CardBody className="p-4">
                            <Form onSubmit={handleSubmit}>
                                <FormGroup className="mb-3">
                                    <Label className="small fw-bold">Amount (₹) *</Label>
                                    <InputGroup>
                                        <InputGroupText><FiDollarSign /></InputGroupText>
                                        <Input type="number" step="0.01" value={paymentData.amount} 
                                            onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})} />
                                    </InputGroup>
                                    <small className="text-muted">Max limit: ₹{meta.allowed_max.toFixed(2)}</small>
                                </FormGroup>

                                <FormGroup className="mb-3">
                                    <Label className="small fw-bold">Payment Method *</Label>
                                    <Input type="select" value={paymentData.payment_method} 
                                        onChange={(e) => setPaymentData({...paymentData, payment_method: e.target.value})}>
                                        <option value="cash">Cash</option>
                                        <option value="upi">UPI</option>
                                        <option value="bank">Bank Transfer</option>
                                    </Input>
                                </FormGroup>

                                <FormGroup className="mb-4">
                                    <Label className="small fw-bold">Note</Label>
                                    <Input type="textarea" value={paymentData.note} 
                                        onChange={(e) => setPaymentData({...paymentData, note: e.target.value})} />
                                </FormGroup>

                                <Button color="success" block className="py-2 rounded-3" type="submit">Update Payment</Button>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default EditPayment;